import { useEffect, useMemo, useState } from 'react'
import { api } from '../api/client'
import type { Project, ScheduleTask } from '../types'
import { addBusinessDays, fmtDateShort } from '../lib/format'
import ProgressBar from '../components/ProgressBar'

export default function ScheduleTab({ project }: { project: Project }) {
  const [tasks, setTasks] = useState<ScheduleTask[]>([])
  const [startDate, setStartDate] = useState<string>(project.start_date ?? '')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    setLoading(true)
    api.schedule
      .list(project.id)
      .then((t) => setTasks(t))
      .finally(() => setLoading(false))
  }, [project.id])

  // Compute start/end per task from project.start_date + cumulative durations.
  const computed = useMemo(() => {
    if (!startDate) return tasks.map((t) => ({ task: t, start: null as Date | null, end: null as Date | null }))
    let cursor = new Date(startDate)
    return tasks.map((t) => {
      const start = new Date(cursor)
      const end = addBusinessDays(cursor, t.duration_business_days)
      cursor = new Date(end)
      return { task: t, start, end }
    })
  }, [tasks, startDate])

  const totalDays = computed.reduce((s, c) => s + c.task.duration_business_days, 0)
  const completed = tasks.filter((t) => t.completed).length
  const phases = useMemo(() => {
    const groups = new Map<string, typeof computed>()
    for (const c of computed) {
      const arr = groups.get(c.task.phase) ?? []
      arr.push(c)
      groups.set(c.task.phase, arr)
    }
    return [...groups.entries()].map(([phase, items]) => ({ phase, items }))
  }, [computed])

  async function toggle(t: ScheduleTask) {
    const updated = await api.schedule.update(t.id, { completed: t.completed ? 0 : 1 } as Partial<ScheduleTask>)
    setTasks((prev) => prev.map((x) => (x.id === t.id ? updated : x)))
  }

  async function saveStartDate(d: string) {
    setStartDate(d)
    await api.projects.update(project.id, { start_date: d })
  }

  return (
    <section>
      <div className="row-between mb-3">
        <div>
          <h2>Master Schedule</h2>
          <p className="muted small">
            {phases.length} phases · {tasks.length} tasks · {completed} done · {totalDays} business days total
          </p>
        </div>
        <div className="text-right">
          <label className="muted small block">Ground-break</label>
          <input
            type="date"
            value={startDate ?? ''}
            onChange={(e) => saveStartDate(e.target.value)}
            className="date-input"
          />
        </div>
      </div>

      <div className="card mb-3">
        <ProgressBar pct={tasks.length ? (completed / tasks.length) * 100 : 0} />
        <p className="muted small mt-2">
          {tasks.length ? Math.round((completed / tasks.length) * 100) : 0}% complete
        </p>
      </div>

      {loading && <p className="muted">Loading…</p>}

      {phases.map(({ phase, items }) => {
        const phaseDone = items.every((i) => i.task.completed)
        return (
          <div key={phase} className={`phase-box ${phaseDone ? 'done' : ''}`}>
            <div className="phase-hdr">
              <span className="phase-name">{phase}</span>
              <span className="phase-date">
                {items[0].start ? fmtDateShort(items[0].start.toISOString()) : '—'} →{' '}
                {items[items.length - 1].end ? fmtDateShort(items[items.length - 1].end!.toISOString()) : '—'}
              </span>
            </div>
            <ul className="task-list">
              {items.map(({ task, start, end }) => (
                <li
                  key={task.id}
                  className={`task-row ${task.completed ? 'done' : ''} ${task.is_inspection ? 'inspect' : ''}`}
                  onClick={() => toggle(task)}
                >
                  <span className={`chk ${task.completed ? 'on' : ''}`}>{task.completed ? '✓' : ''}</span>
                  <span className="task-name">
                    {task.is_inspection ? '⚑ ' : ''}
                    {task.task_name}
                  </span>
                  <span className="task-dur muted">{task.duration_business_days}d</span>
                  <span className="task-dates muted small">
                    {start && end
                      ? `${fmtDateShort(start.toISOString())}–${fmtDateShort(end.toISOString())}`
                      : ''}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )
      })}
    </section>
  )
}
